import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaExternalLinkAlt, FaUser } from 'react-icons/fa';
import { useNotification } from '../../context/NotificationContext';
import { CATEGORY_METADATA, PRIORITY_METADATA, NOTIFICATION_TYPES } from '../../api/notifications';
import { resolveNotificationRoute } from '../../utils/notificationRoutes';
import TeamInvitationNotification from './TeamInvitationNotification';
import styles from './NotificationItem.module.css';

/**
 * NotificationItem Component
 * 
 * Individual notification item with:
 * - Priority-based styling
 * - Category indicators
 * - Action buttons
 * - Time formatting
 * - Read/unread states
 * - Hover effects
 * - Accessibility features
 */
const NotificationItem = ({ 
  notification, 
  onClick, 
  onRead, 
  showCategory = false,
  showActions = true,
  compact = false 
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const navigate = useNavigate();
  
  const { 
    markAsRead, 
    deleteNotification, 
    utils 
  } = useNotification();
  
  // Safety check for notification object
  if (!notification) {
    return null;
  }

  // Handle team invitation notifications specially
  const isTeamInvitation = notification.type === NOTIFICATION_TYPES.TEAM_INVITATION_SENT;
  
  if (isTeamInvitation) {
    return (
      <TeamInvitationNotification 
        notification={notification}
        onResponse={(response, data) => {
          onRead?.(); // Refresh the notification list
        }}
      />
    );
  }

  /**
   * Handle notification click
   */
  const handleClick = async (event) => {
    // Don't trigger click if clicking on action buttons
    if (event.target.closest(`.${styles.actions}`)) {
      return;
    }

    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification._id);
      onRead?.();
    }

    // Call parent click handler
    onClick?.(notification);

    // Navigate to action URL if available
    const rawActionUrl = utils.getActionUrl(notification);
    if (rawActionUrl && rawActionUrl !== '#') {
      // Resolve the URL to a valid frontend route
      const { url, state } = resolveNotificationRoute(notification, rawActionUrl);
      
      if (url) {
        navigate(url, state ? { state } : undefined);
      }
    }
  };

  /**
   * Handle delete notification
   */
  const handleDelete = async (event) => {
    event.stopPropagation();
    
    if (isDeleting) return;
    
    setIsDeleting(true);
    
    try {
      await deleteNotification(notification._id);
      onRead?.(); // Refresh the list
    } catch {
      setIsDeleting(false);
    }
  };

  /**
   * Handle mark as read toggle
   */
  const handleMarkAsRead = async (event) => {
    event.stopPropagation();
    
    if (!notification.read) {
      await markAsRead(notification._id);
      onRead?.();
    }
  };

  // Get category metadata with safe fallbacks
  const categoryInfo = utils.getCategoryForType(notification?.type || 'system');
  const priorityInfo = utils.getPriorityMetadata(notification?.priority || 'medium');
  
  // Format time with safe fallbacks
  const timeAgo = utils.getTimeAgo(notification?.createdAt || new Date());
  const isRecent = utils.isRecent(notification?.createdAt || new Date());
  
  // Check if has action
  const hasAction = utils.hasAction(notification);
  const actionLabel = utils.getActionLabel(notification);

  // Get triggered by info
  const triggeredBy = notification?.triggeredBy;
  
  // Handle touch events for swipe to delete (mobile)
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    if (!touchStart) return;
    
    const currentTouch = e.targetTouches[0].clientX;
    const diff = touchStart - currentTouch;
    
    if (Math.abs(diff) > 10) {
      setIsSwiping(true);
      // Only allow left swipe (positive diff)
      if (diff > 0) {
        setSwipeOffset(Math.min(diff, 100));
      }
    }
  };
  
  const handleTouchEnd = () => {
    if (swipeOffset > 80) {
      // If swiped more than 80px, trigger delete
      handleDelete({ stopPropagation: () => {} });
    }
    
    setSwipeOffset(0);
    setTouchStart(null);
    setIsSwiping(false);
  };

  return (
    <div
      className={`
        ${styles.notificationItem} 
        ${!notification.read ? styles.unread : ''} 
        ${compact ? styles.compact : ''}
        ${isRecent ? styles.recent : ''}
        ${isDeleting ? styles.deleting : ''}
        ${isSwiping ? styles.swiping : ''}
      `}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-label={`${notification.read ? 'Read' : 'Unread'} notification: ${notification.title}`}
      style={{
        '--priority-color': priorityInfo.color,
        '--priority-bg': priorityInfo.backgroundColor,
        transform: `translateX(-${swipeOffset}px)`,
        transition: isSwiping ? 'none' : 'transform 0.3s ease'
      }}
    >
      {/* Priority indicator */}
      <div 
        className={styles.priorityIndicator}
        style={{ backgroundColor: priorityInfo.color }}
        title={`${priorityInfo.name} priority`}
        aria-label={`${priorityInfo.name} priority`}
      />

      {/* Main container */}
      <div className={styles.mainContainer}>
        {/* Main content */}
        <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            {/* Category icon */}
            {showCategory && (
              <span 
                className={styles.categoryIcon}
                title={categoryInfo.name}
                aria-label={categoryInfo.name}
              >
                {categoryInfo.icon}
              </span>
            )}
            
            {/* Unread badge */}
            {!notification.read && (
              <span className={styles.newBadge} aria-label="New">NEW</span>
            )}
            
            {/* Title */}
            <h4 className={`${styles.title} ${!notification.read ? styles.unreadTitle : ''}`}>
              {notification.title}
              {!notification.read && (
                <span className={styles.unreadDot} aria-label="Unread" />
              )}
            </h4>
            
            {/* Priority badge for urgent notifications */}
            {notification.priority === 'urgent' && (
              <span className={styles.urgentBadge} title="Urgent">
                {priorityInfo.icon}
              </span>
            )}
          </div>
          
          {/* Time and triggered by */}
          <div className={styles.meta}>
            <span className={styles.time} title={new Date(notification.createdAt).toLocaleString()}>
              {timeAgo}
            </span>
            
            {triggeredBy && (
              <>
                <span className={styles.separator}>•</span>
                <span className={styles.triggeredBy} title={`Triggered by ${triggeredBy.name}`}>
                  <FaUser className={styles.userIcon} />
                  {triggeredBy.name}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Message */}
        <div className={styles.message}>
          {notification.message}
        </div>

        {/* Action button removed for cleaner layout */}

        {/* Footer - only show if category badge is enabled */}
        {showCategory && (
          <div className={styles.footer}>
            <span className={styles.categoryBadge}>
              {categoryInfo.name}
            </span>
          </div>
        )}
      </div>
        
        {/* Actions */}
        {showActions && (
          <div className={styles.actions}>
            {/* Mark as read */}
            {!notification.read && (
              <button
                className={styles.actionButton}
                onClick={handleMarkAsRead}
                title="Mark as read"
                aria-label="Mark as read"
              >
                ✓
              </button>
            )}
            
            {/* Delete */}
            <button
              className={`${styles.actionButton} ${styles.deleteButton}`}
              onClick={handleDelete}
              disabled={isDeleting}
              title="Delete notification"
              aria-label="Delete notification"
            >
              {isDeleting ? '⏳' : <FaTrash />}
            </button>
          </div>
        )}
      </div>

      {/* Loading overlay for deleting state */}
      {isDeleting && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner} />
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
